import React, { useState } from 'react';
import {
  Bar,
  BarChart,
  CartesianGrid,
  Legend,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from 'recharts';
import { MatchScorelinePoint } from './types';
import { OUTCOME, CHROME } from './dataViz';

interface Props {
  data: MatchScorelinePoint[];
}

type Mode = 'lines' | 'sets' | 'games';

const MODE_LABEL: Record<Mode, string> = {
  lines: 'Lines',
  sets: 'Sets',
  games: 'Games',
};

const shortOpp = (name: string): string => (name.length > 14 ? name.slice(0, 12) + '…' : name);

const SegmentedControl: React.FC<{ mode: Mode; onChange: (m: Mode) => void }> = ({
  mode,
  onChange,
}) => (
  <div
    role="tablist"
    aria-label="Chart unit"
    className="inline-flex rounded-lg bg-gray-100 dark:bg-gray-700 p-0.5"
  >
    {(['lines', 'sets', 'games'] as const).map((opt) => {
      const active = mode === opt;
      return (
        <button
          key={opt}
          role="tab"
          aria-selected={active}
          onClick={() => onChange(opt)}
          className={`px-3 py-1 text-xs font-medium rounded-md transition ${
            active
              ? 'bg-white dark:bg-gray-800 shadow text-gray-900 dark:text-gray-100'
              : 'text-gray-600 dark:text-gray-300 hover:text-gray-900 dark:hover:text-gray-100'
          }`}
        >
          {MODE_LABEL[opt]}
        </button>
      );
    })}
  </div>
);

const MatchBreakdownChart: React.FC<Props> = ({ data }) => {
  const [mode, setMode] = useState<Mode>('lines');

  if (data.length === 0) {
    return <p className="text-sm text-gray-500 dark:text-gray-400 italic">No match data yet.</p>;
  }

  const wonKey = `${MODE_LABEL[mode]} won`;
  const lostKey = `${MODE_LABEL[mode]} lost`;
  const pickWon = (m: MatchScorelinePoint): number =>
    mode === 'games' ? m.games_won : mode === 'sets' ? m.sets_won : m.lines_won;
  const pickLost = (m: MatchScorelinePoint): number =>
    mode === 'games' ? m.games_lost : mode === 'sets' ? m.sets_lost : m.lines_lost;

  const chartData = data.map((m) => ({
    label: shortOpp(m.opponent_name),
    [wonKey]: pickWon(m),
    [lostKey]: pickLost(m),
  }));

  return (
    <div data-testid="match-breakdown-chart">
      <div className="flex justify-end mb-2">
        <SegmentedControl mode={mode} onChange={setMode} />
      </div>
      <div className="h-64 w-full">
        <ResponsiveContainer width="100%" height="100%">
          <BarChart data={chartData} margin={{ top: 10, right: 20, left: 0, bottom: 30 }}>
            <CartesianGrid strokeDasharray="3 3" stroke={CHROME.grid} />
            <XAxis
              dataKey="label"
              tick={{ fontSize: 11 }}
              interval={0}
              angle={-30}
              textAnchor="end"
              height={60}
            />
            <YAxis allowDecimals={false} tick={{ fontSize: 12 }} />
            <Tooltip />
            <Legend />
            <Bar dataKey={lostKey} stackId="match" fill={OUTCOME.loss} />
            <Bar dataKey={wonKey} stackId="match" fill={OUTCOME.win} />
          </BarChart>
        </ResponsiveContainer>
      </div>
    </div>
  );
};

export default MatchBreakdownChart;
